/**
 * GlobalView — Satellite Info Service
 *
 * Fetches satellite information from Wikipedia's public REST API.
 * No API key required — the Wikipedia API is publicly accessible with CORS support.
 *
 * Strategy:
 * 1. Clean the satellite name (remove suffixes like R/B, DEB, etc.)
 * 2. Try direct page summary lookup
 * 3. Fallback to opensearch if direct lookup fails
 * 4. Cache results in-memory to avoid redundant fetches
 */

// ─── Types ───────────────────────────────────────────────────────

export interface SatelliteWikiInfo {
  /** Wikipedia article title */
  title: string;
  /** Short summary extract (plain text) */
  extract: string;
  /** Thumbnail image URL (if available) */
  thumbnailUrl?: string;
  /** Full Wikipedia article URL */
  articleUrl: string;
}

// ─── Cache ───────────────────────────────────────────────────────

const cache = new Map<string, SatelliteWikiInfo | null>();

// ─── Name Cleaning ───────────────────────────────────────────────

/**
 * Clean satellite name for Wikipedia search.
 * Removes common TLE suffixes and debris markers.
 */
export function cleanSatelliteName(raw: string): string {
  let name = raw.trim();

  // Remove known TLE suffixes (order matters — most specific first)
  name = name.replace(/\s+R\/B\s*$/i, '');
  name = name.replace(/\s+DEB\s*$/i, '');
  name = name.replace(/\s+\(\d+\)\s*$/i, '');
  name = name.replace(/\s+[A-Z]\/[A-Z]\s*$/i, '');

  return name.trim();
}

// ─── Wikipedia API ───────────────────────────────────────────────

const WIKI_SUMMARY_URL = 'https://en.wikipedia.org/api/rest_v1/page/summary';
const WIKI_SEARCH_URL = 'https://en.wikipedia.org/w/api.php';

/**
 * Try to fetch a page summary directly by title.
 */
async function fetchSummary(title: string): Promise<SatelliteWikiInfo | null> {
  try {
    const encoded = encodeURIComponent(title.replace(/ /g, '_'));
    const res = await fetch(`${WIKI_SUMMARY_URL}/${encoded}`, {
      headers: { 'Accept': 'application/json' },
    });

    if (!res.ok) return null;

    const data = await res.json();

    // Skip disambiguation pages
    if (data.type === 'disambiguation') return null;

    // Must have an extract
    if (!data.extract) return null;

    return {
      title: data.title ?? title,
      extract: data.extract,
      thumbnailUrl: data.thumbnail?.source,
      articleUrl: data.content_urls?.desktop?.page ?? `https://en.wikipedia.org/wiki/${encoded}`,
    };
  } catch (err) {
    console.warn('[SatInfoService] Summary fetch failed:', err);
    return null;
  }
}

/**
 * Search Wikipedia for a satellite and return the best match title.
 */
async function searchWikipedia(query: string): Promise<string | null> {
  try {
    const params = new URLSearchParams({
      action: 'opensearch',
      search: query,
      limit: '5',
      namespace: '0',
      format: 'json',
      origin: '*',
    });

    const res = await fetch(`${WIKI_SEARCH_URL}?${params}`);
    if (!res.ok) return null;

    const data = await res.json();
    const titles = data[1] as string[];

    if (!titles || titles.length === 0) return null;

    // Prefer results that contain "satellite" or the original name
    const lowerQuery = query.toLowerCase();
    const preferred = titles.find(
      (t) => t.toLowerCase().includes('satellite') || t.toLowerCase().includes(lowerQuery),
    );

    return preferred ?? titles[0];
  } catch (err) {
    console.warn('[SatInfoService] Search failed:', err);
    return null;
  }
}

// ─── Public API ──────────────────────────────────────────────────

/**
 * Fetch satellite information from Wikipedia.
 * Returns cached result if available.
 *
 * @param satelliteName - The satellite name (e.g. "ISS (ZARYA)", "STARLINK-1234")
 * @returns SatelliteWikiInfo or null if not found
 */
export async function fetchSatelliteInfo(
  satelliteName: string,
): Promise<SatelliteWikiInfo | null> {
  // Check cache first
  if (cache.has(satelliteName)) {
    return cache.get(satelliteName) ?? null;
  }

  const cleanName = cleanSatelliteName(satelliteName);

  // Strategy 1: Direct summary lookup with cleaned name
  let info = await fetchSummary(cleanName);

  // Strategy 2: Try with "satellite" suffix for disambiguation
  if (!info) {
    info = await fetchSummary(`${cleanName} (satellite)`);
  }

  // Strategy 3: Fallback to search
  if (!info) {
    const searchTitle = await searchWikipedia(`${cleanName} satellite`);
    if (searchTitle) {
      info = await fetchSummary(searchTitle);
    }
  }

  // Cache the result (also cache null to avoid re-fetching misses)
  cache.set(satelliteName, info);

  return info;
}

/**
 * Clear the info cache (e.g. for testing).
 */
export function clearSatelliteInfoCache(): void {
  cache.clear();
}
